import React, { createContext, useContext } from "react"
import compose from "recompose/compose"
import withProps from "recompose/withProps"
import { withApollo } from "@apollo/react-hoc"
import { withBenchmarkSettings } from "./withBenchmarkSettings"
import withBenchmarkUrlProps from "./helpers/withBenchmarkUrlProps"
import {
  defaultIndustry,
  defaultCompanySize,
  recruiterTypes,
} from "./helpers/defaults"
import useBenchmarkSettingsState from "./helpers/useBenchmarkSettingsState"
import useUpdateUrlOnBenchmarkSettingsChange from "./helpers/useUpdateUrlOnBenchmarkSettingsChange"
import COMPANY_SIZES_QUERY from "./CompanySizesQuery.gql"
import BENCHMARK_SETTINGS_MUTATION from "./BenchmarkSettingsMutation.gql"

const BenchmarkSettingsContext = createContext()

const useBenchmarkSettingsContext = () => {
  const context = useContext(BenchmarkSettingsContext)

  if (!context) {
    throw new Error(
      "useBenchmarkSettingsContext must be used within a BenchmarkSettingsProvider",
    )
  }

  return context
}

const nothing = () => {}

const BenchmarkSettingsPureProvider = ({
  benchmarkSettings = {},
  benchmarkSettingsRefetch,
  children,
  client,
  benchmarkSettingsLoading,
  industries = [],
  initialRecruiterType,
  initialIndustryId,
  initialCompanySizeId,
  onChangeRecruiterType = nothing,
  onChangeIndustry = nothing,
  onChangeCompanySize = nothing,
  fetchCompanySizes = _ => async () => new Promise(resolve => resolve([])),
  onSetBenchmarkSettings = _ => async () => new Promise(resolve => resolve({})),
}) => {
  if (benchmarkSettingsLoading) return null

  const {
    state,
    setRecruiterType,
    setIndustry,
    setCompanySize,
    setBenchmarkSettings,
    // eslint-disable-next-line
  } = useBenchmarkSettingsState({
    benchmarkSettings,
    benchmarkSettingsRefetch,
    industries,
    initialRecruiterType,
    initialIndustryId,
    initialCompanySizeId,
    fetchCompanySizes: fetchCompanySizes(client),
    onSetBenchmarkSettings: onSetBenchmarkSettings(client),
  })

  // eslint-disable-next-line
  useUpdateUrlOnBenchmarkSettingsChange({
    state,
    onChangeRecruiterType,
    onChangeIndustry,
    onChangeCompanySize,
  })

  if (state.loading) return null

  return (
    <BenchmarkSettingsContext.Provider
      value={{
        benchmarkSettings: state.benchmarkSettings,
        industries: state.industries,
        companySizes: state.companySizes,
        recruiterTypes,
        setBenchmarkSettings,
        setCompanySize,
        setIndustry,
        setRecruiterType: recruiterType => {
          setRecruiterType(recruiterType)
          recruiterType === "headhunter" && setIndustry(defaultIndustry)
        },
      }}
    >
      {children}
    </BenchmarkSettingsContext.Provider>
  )
}

BenchmarkSettingsPureProvider.defaultProps = {
  onChangeRecruiterType: Function.prototype,
  onChangeIndustry: Function.prototype,
  onChangeCompanySize: Function.prototype,
}

const fetchCompanySizes = client => async ({ recruiterType, industry }) => {
  const industryId =
    industry && industry !== defaultIndustry ? industry.id : null
  const { data, errors } = await client.query({
    query: COMPANY_SIZES_QUERY,
    variables: { recruiterType, industryId },
  })

  if (errors) return [defaultCompanySize]

  return data.companySizes
}

const onSetBenchmarkSettings = client => async ({ companySize, industry }) => {
  const {
    data: { updateBenchmarkSettings = {} },
  } = await client.mutate({
    mutation: BENCHMARK_SETTINGS_MUTATION,
    variables: {
      companySizeId: companySize.id,
      industryId: industry.id,
    },
  })

  return updateBenchmarkSettings.success
}

const BenchmarkSettingsProvider = compose(
  withApollo,
  withBenchmarkSettings,
  withProps(() => ({
    fetchCompanySizes,
    onSetBenchmarkSettings,
  })),
)(BenchmarkSettingsPureProvider)

const BenchmarkSettingsFromUrlProvider = withBenchmarkUrlProps(
  BenchmarkSettingsProvider,
)

export {
  BenchmarkSettingsProvider,
  BenchmarkSettingsFromUrlProvider,
  BenchmarkSettingsPureProvider,
  useBenchmarkSettingsContext,
  BenchmarkSettingsContext,
}
